// src/modules/app-views/customers/pages/Document/actions/dropdownMenu.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { MoreOutlined, ImportOutlined } from '@ant-design/icons';
import {
  dropdownArchive,
  dropdownDivider,
  dropdownDuplicate,
  dropdownNew,
  dropdownRemove,
  dropdownRemovePermanently,
  dropdownRestore,
} from 'layouts/Pages/PageHeader/dropdownMenus';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

// *******************************************************************************
// CUSTOM DROPDOWN ITEMS
// *******************************************************************************

// Migration divider - hidden when no migration tokens
const migrationDivider = {
  key: 'migration-divider',
  type: 'divider',
  hidden: ({ storedSubscription }) => {
    const tokens = storedSubscription?.migrationTokens || [];
    return tokens.length === 0;
  },
};

// Reimport document - visible when migration token exists
const customerDropdownReimport = {
  key: 'reimport',
  label: 'reimport_document',
  icon: <ImportOutlined />,
  hidden: ({ storedSubscription }) => {
    const tokens = storedSubscription?.migrationTokens || [];
    return tokens.length === 0;
  },
  disabled: ({ currentPage }) => {
    const data = currentPage?.data;
    return !data?.legacyCode;
  },
  onClick: async ({ page, refreshDocument }) => {
    const data = page?.data;
    const legacyCode = data?.legacyCode;

    if (!legacyCode) {
      appService.message({ flag: 'w', text: translateX('reimport_no_legacy_code') });
      return;
    }

    appService.message({ flag: 'loading', text: translateX('reimporting_document') });

    try {
      const response = await axiosService.post({
        url: API.MIGRATION.REIMPORT,
        data: {
          legacyCode: String(legacyCode),
          collection: 'clientes', // Source collection name (Portuguese)
        },
      });

      const [result] = response || [];
      appService.messageDestroy();

      if (result?.success) {
        appService.notification({
          flag: 'success',
          title: translateX('reimport_success_title'),
          description: translateX('reimport_success_description', {
            number: legacyCode,
          }),
          duration: 6,
        });
        refreshDocument?.();
      } else {
        const errorCode = result?.errorCode;
        if (errorCode === 'UPDATE_NOT_ALLOWED') {
          appService.notification({
            flag: 'warning',
            title: translateX('document_imported_failure'),
            description: translateX('document_imported_failure_description', {
              documentNumber: legacyCode,
            }),
            duration: 10,
          });
        } else {
          appService.notification({
            flag: 'warning',
            title: translateX('reimport_error_title'),
            description: translateX('document_not_found_description', {
              documentNumber: legacyCode,
            }),
            duration: 8,
          });
        }
      }
    } catch (error) {
      appService.messageDestroy();
      const errorData = error?.response?.data?.data || error?.response?.data || {};
      const errorCode = errorData?.errorCode;

      if (errorCode === 'UPDATE_NOT_ALLOWED') {
        appService.notification({
          flag: 'warning',
          title: translateX('document_imported_failure'),
          description: translateX('document_imported_failure_description', {
            documentNumber: legacyCode,
          }),
          duration: 10,
        });
      } else {
        appService.notification({
          flag: 'error',
          title: translateX('reimport_error_title'),
          description: errorData?.message || error?.message || translateX('unknown_error'),
          duration: 8,
        });
      }
    }
  },
};

// *******************************************************************************
// DROPDOWN MENU CONFIGURATION
// *******************************************************************************
const customersDocumentDropdownMenu = () => {
  return {
    action: 'dropdownMenu',
    icon: <MoreOutlined />,
    tooltip: 'more_options',

    dropdownItems: [
      dropdownNew,
      dropdownDuplicate,
      migrationDivider,
      customerDropdownReimport,
      dropdownDivider,
      dropdownArchive,
      dropdownRemove,
      dropdownRestore,
      dropdownRemovePermanently,
    ],
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default customersDocumentDropdownMenu;
